import { makeStyles, Typography } from '@paintscout/ui'
import { EstimatorAvatar, useClientOptions, useUsers } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import React from 'react'
import { getCrews } from '@paintscout/util/builder'
import { DayEvent } from './DayEvent'

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  header: {
    padding: theme.spacing(1.5, 2),
    borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  managerName: {
    lineHeight: 1.2
  },
  eventWrapper: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P96}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  }
}))

interface ProjectsByManagerProps {
  events: CalendarEvent[]
}

export function ProjectsByManager({ events }: ProjectsByManagerProps) {
  const classes = useStyles()
  const { users, userPictures } = useUsers()
  const { options } = useClientOptions()
  const crews = getCrews({ options })
  const crew = crews.find((c) => c._id === events[0].crew)
  const projectManager = users.find((u) => u.user_id === crew?.projectManager?.id)

  // order events by total duration ascending
  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <EstimatorAvatar
          user={{
            id: projectManager?.user_id,
            firstName: projectManager?.user_metadata?.firstName,
            lastName: projectManager?.user_metadata?.lastName
          }}
          userPictures={userPictures}
          width={'32px'}
          height={'32px'}
        />
        <div>
          <Typography className={classes.managerName}>{projectManager?.name}</Typography>
          <Typography variant="caption" color="textSecondary">
            {orderedEvents.length} {orderedEvents.length === 1 ? 'job' : 'jobs'}
          </Typography>
        </div>
      </div>
      <div>
        {orderedEvents.map((event) => (
          <div className={classes.eventWrapper} key={event.id}>
            <DayEvent event={event} />
          </div>
        ))}
      </div>
    </div>
  )
}
